import type { PartId, ProbeOutcome, ScanResult, SignalValue } from './types';
import { runProbes } from './runner';
import { assembleParts } from './parts';
import { computeScore, publicReasons } from './scoring';
import { getFingerprintId } from './fingerprintId';
import { collectCrossContext } from './crossContext';
import { getRegistryCollisions } from './registry';
import { stableStringify } from './hash';

export interface ScanHandle {
  /** Resolves with the assembled result. Never rejects. */
  result: Promise<ScanResult>;
  cancel(): void;
}

let cancelled = false;

/**
 * Executes a full scan.
 *
 * Assembles fingerprint parts, score, cross-context divergences, and the
 * fingerprint id. A single failed probe degrades the status to PARTIAL rather
 * than failing the whole scan.
 */
export async function runScan(opts: { gestureGranted?: boolean } = {}): Promise<ScanResult> {
  cancelled = false;
  const startedAt = Date.now();

  const outcomes: ProbeOutcome[] = [];
  await runProbes(
    {
      onOutcome: (o) => {
        if (cancelled) return;
        outcomes.push(o);
      },
      onComplete: () => {
        /* no-op */
      },
    },
    { globalDeadlineMs: 30_000, gestureGranted: opts.gestureGranted ?? false },
  );

  const { parts, fingerprintHash } = await assembleParts(outcomes);
  const findings = outcomes.flatMap((o) => o.findings);

  const crossContext = await collectCrossContext().catch(() => ({
    available: false,
    contexts: ['main' as const],
    divergences: [],
  }));

  // Cross-context divergences become browser-category findings, so they feed
  // the score like any other evidence.
  for (const d of crossContext.divergences) {
    findings.push({
      id: `crosscontext.${d.signal}`,
      severity: 'high',
      category: 'browser',
      layer: 'browser',
      confidence: 'high',
      title: `Value differs across execution contexts: ${d.signal}`,
      detail:
        'A genuine browser reports identical navigator/device values in the main page, ' +
        'iframes, and workers. A mismatch usually means the value was patched in only ' +
        'some contexts (a common anti-detect / automation shortcut).',
      sources: [],
      evidence: [`main=${stableStringify(d.main)}`, `iframe=${stableStringify(d.iframe)}`, `worker=${stableStringify(d.worker)}`],
      scoreContribution: 25,
    });
  }

  const { score, reasons } = computeScore(findings);

  // A probe-id collision is a build bug. Surface it as a visible finding rather
  // than letting it silently drop a probe.
  for (const c of getRegistryCollisions()) {
    findings.push({
      id: `registry.duplicate-${c.id}`,
      severity: 'medium',
      category: 'browser',
      layer: 'browser',
      confidence: 'high',
      title: `Duplicate probe id: ${c.id}`,
      detail:
        'Two probes registered the same id, so one of them was dropped. This is a bug in the probe registry, not a property of your browser.',
      sources: [],
      evidence: [`id=${c.id}`],
      scoreContribution: 0,
    });
  }

  const signals: Record<string, Record<string, SignalValue>> = {};
  for (const o of outcomes) {
    signals[o.probe.id] = o.signals;
  }

  const failed = outcomes.filter((o) => o.state === 'error' || o.state === 'timeout');
  const done = outcomes.filter((o) => o.state === 'done');
  const status =
    failed.length === 0 && done.length > 0
      ? 'COLLECT_SUCCESSFUL'
      : done.length > 0
        ? 'COLLECT_PARTIAL'
        : 'COLLECT_FAILED';

  return {
    status,
    fingerprintHash,
    fingerprintId: getFingerprintId(),
    fingerprintParts: parts,
    botScore: score,
    reasons: publicReasons(reasons),
    signals,
    findings,
    crossContext,
    startedAt,
    finishedAt: Date.now(),
  };
}

export function cancelScan(): void {
  cancelled = true;
}

export type { PartId };
